import * as yup from 'yup';

import { ERRORS_TEXTS, FIELDS_LIMITS } from '../../../../const/validation';
import { UserType } from '../../../../types/aggregateUser';

const isAuthor = (role: string) => role === '0';

const isPerson = (role: string, type: string) => {
  return isAuthor(role) && Number(type) !== UserType.Org;
};

const isRepresentative = (role: string, type: string) => {
  return isAuthor(role) && Number(type) === UserType.Representative;
};

const isOrg = (role: string, type?: string) => {
  return isAuthor(role) && type === String(UserType.Org);
};

const isInsider = (role: string, type?: string) => {
  return isAuthor(role) && type === String(UserType.Insider);
};

const notRepresentative: (role: string, type: string) => boolean = (role, type) => {
  return !isRepresentative(role, type);
};

export const schema = yup.object({
  login: yup.string().default('').required(ERRORS_TEXTS.required),
  alias: yup.string().nullable().default(null),
  role: yup.string().required(ERRORS_TEXTS.required),
  type: yup.mixed<UserType>().required(ERRORS_TEXTS.required),
  org: yup
    .string()
    .nullable()
    .default(null)
    .when(['role', 'type'], {
      is: isRepresentative,
      then: yup.string().required(ERRORS_TEXTS.required),
      otherwise: yup.string().nullable().default(null),
    }),
  orgName: yup
    .string()
    .nullable()
    .default(null)
    .when(['role', 'type'], {
      is: isOrg,
      then: yup.string().nullable().required(ERRORS_TEXTS.required),
      otherwise: yup.string().nullable().default(null),
    }),
  inn: yup
    .string()
    .nullable()
    .default(null)
    .matches(/([0-9]{10})/, {
      message: ERRORS_TEXTS.formatInn,
      excludeEmptyString: true,
    })
    .notRequired(),
  ogrn: yup
    .string()
    .nullable()
    .default(null)
    .matches(/^[0-9]{13}$/, {
      message: ERRORS_TEXTS.formatOgrn,
      excludeEmptyString: true,
    })
    .notRequired(),
  kpp: yup
    .string()
    .nullable()
    .default(null)
    .matches(/^[0-9]{9}$/, {
      message: ERRORS_TEXTS.formatKpp,
      excludeEmptyString: true,
    })
    .notRequired(),
  lastName: yup
    .string()
    .nullable()
    .default(null)
    .when(['role', 'type'], {
      is: isPerson,
      then: yup.string().required(ERRORS_TEXTS.required),
      otherwise: yup.string().nullable().default(null),
    }),
  firstName: yup
    .string()
    .nullable()
    .default(null)
    .when(['role', 'type'], {
      is: isPerson,
      then: yup.string().required(ERRORS_TEXTS.required),
      otherwise: yup.string().nullable().default(null),
    }),
  midName: yup.string().nullable().default(null),
  position: yup.string().nullable().default(null),
  agreementNum: yup.string().nullable().default(null),
  employeeId: yup
    .string()
    .nullable()
    .default(null)
    .when(['role', 'type'], {
      is: isInsider,
      then: yup.string().nullable().required(ERRORS_TEXTS.required),
      otherwise: yup.string().nullable().default(null),
    }),
  passportId: yup
    .string()
    .nullable()
    .default(null)
    .matches(/^[0-9]{2} ?[0-9]{2} ?[0-9]{6}$/, {
      message: ERRORS_TEXTS.formatPassport,
      excludeEmptyString: true,
    }),
  passportIssued: yup.string().nullable().default(null),
  issueDate: yup.date().nullable().default(null),
  birthDate: yup.date().nullable().default(null),
  placeOfBirth: yup.string().nullable().default(null),
  hasComission: yup.boolean().default(false),
  conflictOfInterest: yup.boolean().default(false),
  photo: yup.string().nullable().default(null),
  shortinfoHtml: yup.string().nullable().default(null).max(FIELDS_LIMITS.infoMaxLength, ERRORS_TEXTS.infoMaxLength),
  infoHtml: yup.string().nullable().default(null).max(FIELDS_LIMITS.infoMaxLength, ERRORS_TEXTS.infoMaxLength),
  shortinfoText: yup.string().nullable().default(null).max(FIELDS_LIMITS.infoMaxLength, ERRORS_TEXTS.infoMaxLength),
  infoText: yup.string().nullable().default(null).max(FIELDS_LIMITS.infoMaxLength, ERRORS_TEXTS.infoMaxLength),
  phone: yup
    .string()
    .nullable()
    .default(null)
    .when(['role', 'type'], {
      is: notRepresentative,
      then: yup.string().matches(/^\+\d\s?\d{3}\s?\d{3}\s?\d\d\s?\d\d$/, ERRORS_TEXTS.required),
      otherwise: yup.string().nullable().default(null),
    }),
  email: yup
    .string()
    .nullable()
    .default(null)
    .when(['role', 'type'], {
      is: notRepresentative,
      then: yup.string().email(ERRORS_TEXTS.emailInvalid).required(ERRORS_TEXTS.required),
      otherwise: yup.string().nullable().default(null),
    }),
  rating: yup
    .number()
    .transform((value, originalValue) => {
      if (originalValue === null || originalValue === undefined || originalValue === '') return null;

      const normalizedValue = typeof originalValue === 'string' ? originalValue.replace(',', '.') : originalValue;
      const parsedValue = Number(normalizedValue);

      return Number.isNaN(parsedValue) ? value : parsedValue;
    })
    .nullable()
    .default(null)
    .min(3.7, 'Минимальное значение 3.70')
    .max(5.0, 'Максимальное значение 5.00'),
  password: yup
    .string()
    .nullable()
    .default(null)
    .min(FIELDS_LIMITS.passwordMinLength, ERRORS_TEXTS.passwordMinLength)
    .max(FIELDS_LIMITS.passwordMaxLength, ERRORS_TEXTS.passwordMaxLength)
    .when('changePassword', {
      is: true,
      then: yup.string().required(ERRORS_TEXTS.required),
    }),
  confirmPassword: yup
    .string()
    .nullable()
    .default(null)
    .oneOf([yup.ref('password'), null], ERRORS_TEXTS.passwordMatches)
    .min(FIELDS_LIMITS.passwordMinLength, ERRORS_TEXTS.passwordMinLength)
    .max(FIELDS_LIMITS.passwordMaxLength, ERRORS_TEXTS.passwordMaxLength)
    .when('changePassword', {
      is: true,
      then: yup.string().required(ERRORS_TEXTS.required),
    }),
});
